import { useEffect, useRef } from 'react';
import { useDispatch, useSelector, type TypedUseSelectorHook } from 'react-redux';

import type { RootState, AppDispatch } from '../store';
import { selectMarkers, setMarkers } from '../slices/mapSlice';
import type { MarkerData } from '../types/map.types';
import {
  DISPLAY_PLACE_ON_MAP_TOOL_NAME,
  DISPLAY_PLACE_ON_MAP_TOOL_PART_TYPE,
  type AddToolOutputFn,
  type DisplayPlaceOnMapToolPart,
  type MessageLike,
  type UseMapToolInterceptorReturn,
} from '../types/mapTool.types';

/**
 * Typed dispatch hook for the store.
 * Defined locally to avoid a circular dependency with `@loci/hooks`.
 */
const useAppDispatch: () => AppDispatch = useDispatch;

/**
 * Typed selector hook for the store.
 * Defined locally to avoid a circular dependency with `@loci/hooks`.
 */
const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

/**
 * Placeholder latitude/longitude used when a `display_place_on_map` tool part
 * reaches `input-available` without lat/lng in its payload. In practice the
 * Zod schema on the server already enforces both fields, but the client
 * input type marks them as optional so the hook has a total fallback. Real
 * coordinates arrive via the `useGetPlaceDetailsQuery` cache when the card
 * mounts, so a temporary placeholder does not affect user-visible map state.
 */
const PLACEHOLDER_LAT = 0;
const PLACEHOLDER_LNG = 0;

/**
 * Intercepts parallel `display_place_on_map` tool invocations emitted by
 * Gemini in a single assistant turn and (1) batches every new marker into a
 * single `setMarkers` dispatch, (2) fires `addToolOutput` exactly once per
 * `toolCallId` so the streaming chat can complete its turn.
 *
 * Why a single dispatch? Gemini emits multiple tool calls in parallel for
 * recommendation-style queries ("three coffee shops nearby"). Dispatching
 * per-marker would re-render the map once per call; batching keeps the map
 * at one commit per message-parts change, as verified by the React
 * Profiler acceptance criterion on FRE-669.
 *
 * The hook is idempotent across re-renders because acknowledged
 * `toolCallId`s are tracked in a `useRef<Set<string>>`, and because the
 * `setMarkers` dispatch only fires when the dedup pass produces at least
 * one new marker not already present in `state.map.markers`.
 */
export function useMapToolInterceptor(
  messages: readonly MessageLike[],
  addToolOutput: AddToolOutputFn,
): UseMapToolInterceptorReturn {
  const dispatch = useAppDispatch();
  const existingMarkers = useAppSelector(selectMarkers);
  const acknowledgedRef = useRef<Set<string>>(new Set());
  // Separate guard for the dispatch path: acknowledgments cover the tool-call
  // side but do not prevent re-dispatching a `placeId` after an external
  // `clearMap()` wipes `existingMarkers`. Without this ref, the next
  // messages-reference change (e.g., the user adding a follow-up query) would
  // re-walk the previous assistant message and re-add the cleared markers.
  const dispatchedPlaceIdsRef = useRef<Set<string>>(new Set());
  // Keep the latest callback in a ref so the effect does not re-run when a
  // consumer passes an unstable (inline) `addToolOutput` each render.
  const addToolOutputRef = useRef<AddToolOutputFn>(addToolOutput);
  useEffect(() => {
    addToolOutputRef.current = addToolOutput;
  }, [addToolOutput]);

  useEffect(() => {
    const latestAssistant = findLatestAssistantMessage(messages);
    if (!latestAssistant) return;

    const toolParts = filterDisplayPlaceOnMapParts(latestAssistant.parts);
    if (toolParts.length === 0) return;

    // 1. Batched dispatch — seen-set combines Redux markers, prior dispatches
    // this hook has fired (so `clearMap()` does not cause stale markers to be
    // re-dispatched on the next `messages` reference change), and within-
    // message repeats. Fires `setMarkers` at most once per parts change.
    const seenPlaceIds = new Set<string>([
      ...existingMarkers.map((marker) => marker.placeId),
      ...dispatchedPlaceIdsRef.current,
    ]);
    const newMarkers: MarkerData[] = [];

    for (const part of toolParts) {
      const { placeId } = part.input;
      if (seenPlaceIds.has(placeId)) continue;
      seenPlaceIds.add(placeId);
      newMarkers.push(toMarker(part));
    }

    if (newMarkers.length > 0) {
      for (const marker of newMarkers) {
        dispatchedPlaceIdsRef.current.add(marker.placeId);
      }
      dispatch(setMarkers([...existingMarkers, ...newMarkers]));
    }

    // 2. Tool-result acknowledgment — fire `addToolOutput` once per
    // `toolCallId`, regardless of whether that call contributed a new
    // marker. Acknowledged ids are persisted across renders so a
    // re-render with the same `messages` array is a no-op. If the
    // callback throws or rejects, the id is removed so the next render
    // retries — keeps the streaming turn from stalling on a transient
    // error.
    for (const part of toolParts) {
      const { toolCallId } = part;
      if (acknowledgedRef.current.has(toolCallId)) continue;
      acknowledgedRef.current.add(toolCallId);
      try {
        const result = addToolOutputRef.current({
          tool: DISPLAY_PLACE_ON_MAP_TOOL_NAME,
          toolCallId,
          output: { success: true },
        });
        if (result && typeof (result as Promise<void>).catch === 'function') {
          (result as Promise<void>).catch(() => {
            acknowledgedRef.current.delete(toolCallId);
          });
        }
      } catch {
        acknowledgedRef.current.delete(toolCallId);
      }
    }
    // `dispatch` is stable (Redux guarantee). Intentionally omit
    // `existingMarkers`: including it would re-run this effect right after
    // our own `setMarkers` dispatch. The `dispatchedPlaceIdsRef` +
    // `acknowledgedRef` guards above are the source of truth for "already
    // handled", not the Redux marker array.
  }, [messages]); // eslint-disable-line react-hooks/exhaustive-deps
}

function findLatestAssistantMessage(messages: readonly MessageLike[]): MessageLike | undefined {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const message = messages[i];
    if (message.role === 'assistant') return message;
  }
  return undefined;
}

function filterDisplayPlaceOnMapParts(parts: readonly unknown[]): DisplayPlaceOnMapToolPart[] {
  const matched: DisplayPlaceOnMapToolPart[] = [];
  for (const part of parts) {
    if (!isDisplayPlaceOnMapToolPart(part)) continue;
    matched.push(part);
  }
  return matched;
}

function isDisplayPlaceOnMapToolPart(value: unknown): value is DisplayPlaceOnMapToolPart {
  if (!isPlainObject(value)) return false;
  if (value.type !== DISPLAY_PLACE_ON_MAP_TOOL_PART_TYPE) return false;
  if (typeof value.toolCallId !== 'string' || value.toolCallId.length === 0) return false;
  if (value.state !== 'input-available' && value.state !== 'output-available') return false;
  if (!isPlainObject(value.input)) return false;
  const { placeId } = value.input;
  if (typeof placeId !== 'string' || placeId.length === 0) return false;
  return true;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function toMarker(part: DisplayPlaceOnMapToolPart): MarkerData {
  const { placeId, latitude, longitude, name } = part.input;
  return {
    placeId,
    lat: typeof latitude === 'number' ? latitude : PLACEHOLDER_LAT,
    lng: typeof longitude === 'number' ? longitude : PLACEHOLDER_LNG,
    ...(typeof name === 'string' && name.length > 0 ? { label: name } : {}),
  };
}
